import { ViewContainerRef } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { combineLatest, Observable, ReplaySubject } from "rxjs";
import { switchMap, take, tap } from "rxjs/operators";
import { Issue } from "src/app/classes";
import { IssueTransaction, UploadFileTask } from "./classes";
import { IssueTransactionDialog2Component } from "./components/dialog/issue-transaction-dialog2/issue-transaction-dialog2.component";
import { AssignUserFormComponent } from "./components/embedded/assign-user-form/assign-user-form.component";
import { BookIssueItemsFormComponent } from "./components/embedded/book-issue-items-form/book-issue-items-form.component";
import { FolderStructureComponent } from "./components/embedded/folder-structure/folder-structure.component";
import { IssueFormComponent } from "./components/embedded/issue-form/issue-form.component";
import { MakeAppointmentFormSummaryComponent } from "./components/embedded/make-appointment-form-summary/make-appointment-form-summary.component";
import { MakeAppointmentFormComponent } from "./components/embedded/make-appointment-form/make-appointment-form.component";
import { NoteFormComponent } from "./components/embedded/note-form/note-form.component";
import { NotificationBuilderSummaryComponent } from "./components/embedded/notification-builder-summary/notification-builder-summary.component";
import { NotificationBuilderComponent } from "./components/embedded/notification-builder/notification-builder.component";
import { PdfPreviewComponent } from "./components/embedded/pdf-preview/pdf-preview.component";
import { UploadFormSummaryComponent } from "./components/embedded/upload-form-summary/upload-form-summary.component";
import { UploadFormComponent } from "./components/embedded/upload-form/upload-form.component";
import { IIssueTransactionProviderService, IIssueTransactionView, ISSUE_TRANSACTION_PROVIDER_SERVICE, ITransactionSummaryView } from "./interfaces";
import { IssueTransactionService } from "./services/issue-transaction.service";
import { IssueService } from "./services/issue.service";

export class IssueTransactionSetup {
    name: string;
    formGroupName: string;
    formGroup: FormGroup;
    view: IIssueTransactionView;
    summaryView: ITransactionSummaryView;
    formObject: (formBuilder: FormBuilder) => any;
    role: string;
    visibleInConclusion: boolean;
    errors = new ReplaySubject<Object[]>();
    constructor(object) {
        this.name = object.name;
        this.formGroupName = object.formGroupName;
        this.view = object.view;
        this.summaryView = object.summaryView;
        this.formObject = object.formObject;
        this.role = object.role;
        this.visibleInConclusion = object.visibleInConclusion ? true : false;
    }
}

export const IssueTransactionRoles = {
    'BookIssueItems': 'BookIssueItems',
    'Conclusion': 'Conclusion',
    'CreateIssue': 'CreateIssue',
    'EditIssue': 'EditIssue',
    'FolderStructure': 'FolderStructure',
    'LinkMail': 'LinkMail',
    'MakeAppointment': 'MakeAppointment',
    'Notification': 'Notification',
    'Note': 'Note',
    'OperationProtocol': 'OperationProtocol',
    'Upload': 'Upload',
    'User': 'User',
};

export const IssueTransactionMap: { [key: number]: IssueTransactionSetup } = {
    1: new IssueTransactionSetup({
        name: 'Kopfdaten',
        view: IssueFormComponent,
        formObject: (formBuilder: FormBuilder) => formBuilder.group(new Issue),
        visibleInConclusion: true
    }),
    2: new IssueTransactionSetup({
        name: 'Upload',
        view: UploadFormComponent,
        summaryView: UploadFormSummaryComponent,
        formObject: (formBuilder: FormBuilder) => formBuilder.group({ 'upload': formBuilder.control(new UploadFileTask()) }),
        role: IssueTransactionRoles.Upload,
        visibleInConclusion: false
    }),
    3: new IssueTransactionSetup({
        name: 'Termin vereinbaren',
        view: MakeAppointmentFormComponent,
        summaryView: MakeAppointmentFormSummaryComponent,
        formObject: (formBuilder: FormBuilder) => formBuilder.group({ 'appointments': formBuilder.array([]) }),
        role: IssueTransactionRoles.MakeAppointment,
        visibleInConclusion: false
    }),
    4: new IssueTransactionSetup({
        name: 'Download',
        view: FolderStructureComponent,
        formObject: (formBuilder: FormBuilder) => formBuilder.group({ 'download': formBuilder.array([]) }),
        role: IssueTransactionRoles.FolderStructure,
        visibleInConclusion: false
    }),
    5: new IssueTransactionSetup({
        name: 'Nutzer einteilen',
        view: AssignUserFormComponent,
        formObject: (formBuilder: FormBuilder) => formBuilder.group({ 'users': formBuilder.array([]) }),
        role: IssueTransactionRoles.User,
        visibleInConclusion: false
    }),
    6: new IssueTransactionSetup({
        name: 'Benachrichtigungen',
        view: NotificationBuilderComponent,
        formObject: (formBuilder: FormBuilder) => formBuilder.group({ 'notifications': formBuilder.array([]) }),
        role: IssueTransactionRoles.Notification,
        visibleInConclusion: false,
        summaryView: NotificationBuilderSummaryComponent
    }),
    7: new IssueTransactionSetup({
        name: 'Notiz',
        view: NoteFormComponent,
        formObject: (formBuilder: FormBuilder) => formBuilder.group({ 'note': formBuilder.control(null) }),
        role: IssueTransactionRoles.Note,
        visibleInConclusion: false
    }),
    8: new IssueTransactionSetup({
        name: 'Vorgangsartikel',
        view: BookIssueItemsFormComponent,
        formObject: (formBuilder: FormBuilder) => formBuilder.group({ 'tasks': formBuilder.array([]) }),
        visibleInConclusion: false,
        role: IssueTransactionRoles.BookIssueItems
    }),
    9: new IssueTransactionSetup({
        name: 'Vorgangsübersicht',
        view: PdfPreviewComponent,
        formObject: (formBuilder: FormBuilder) => formBuilder.group({ 'url': formBuilder.control(null) }),
        role: IssueTransactionRoles.Conclusion,
        visibleInConclusion: false
    }),
    10: new IssueTransactionSetup({
        name: 'Einsatzprotokoll',
        view: PdfPreviewComponent,
        formObject: (formBuilder: FormBuilder) => formBuilder.group({ 'url': formBuilder.control(null) }),
        role: IssueTransactionRoles.OperationProtocol,
        visibleInConclusion: false
    })
}

export const quillOptions = {
    toolbar: {
        container:
            [
                ['bold', 'italic', 'underline', 'strike'],
                ['code-block'],
                [{ 'header': 1 }, { 'header': 2 }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'font': [] }],
                [{ 'align': [] }],
                ['clean', 'image']
            ]
    }
};

export const initialIssueStates = {
    3: 1
}

export const IssueTransactionStatus = {
    'Initial': 0,
    'Started': 1,
    'Succeeded': 2,
    'Error': 3
}

export const SpecialIssueTransactionRole = {
    NO_ROLE: 0,
    CREATE_ISSUE: 1,
    CREATE_INCIDENT: 2,
    CREATE_CHANGE: 3
}

export const executeIssueTransaction = (issueTransactionService: IssueTransactionService, transactionId: string, viewContainerRef: ViewContainerRef, beforeDialogOpen: (transaction: IssueTransaction) => void = null): Observable<any[]> => {
    return combineLatest([issueTransactionService.getIssueTransactionById(transactionId, true), issueTransactionService.getIssueTransactionAttributes(transactionId), issueTransactionService.getTransactionDefaultMails(transactionId), issueTransactionService.getTransactionDefaultNotifications(transactionId)])
        .pipe(take(1), switchMap(([issueTransaction, attributes, defaultMails, defaultNotifications]) => {
            viewContainerRef.injector.get<IIssueTransactionProviderService>(ISSUE_TRANSACTION_PROVIDER_SERVICE).setIssueTransaction(issueTransaction, attributes, defaultMails, defaultNotifications);
            if (typeof beforeDialogOpen === 'function') beforeDialogOpen(issueTransaction);
            return viewContainerRef.injector.get(MatDialog).open(IssueTransactionDialog2Component, {
                panelClass: "no-padding-dialog-container",
                autoFocus: false,
                viewContainerRef: viewContainerRef
            }).afterClosed();
        }))
        .pipe(tap(([status, issue, steps]) => {
            switch (status) {
                case IssueTransactionStatus.Initial:
                    viewContainerRef.injector.get(MatSnackBar).open('Die Aktion wurde abgebrochen.', 'Ok', {
                        duration: 2000
                    });
                    break;

                case IssueTransactionStatus.Succeeded:
                    if (issue) viewContainerRef.injector.get(Router).navigate(['/issue-details', (issue as Issue).issueid]);
                    break;
            }
        }));
}
